import { createContext, memo, ReactNode, useCallback, useContext } from "react";
import { TComposition } from "shared/types/assetExporter";
import { BrandLogo } from "shared/types/brandsAccounts";
import {
  TColumnValue,
  TInputType,
  TMediaResizeType,
  TValueMappings,
} from "../types";
import { isColumnValue } from "../validators";
import { getBackgroundMediaForUrl } from "./AssetBatchesContext.utils";
import { useAssetBatchesContext } from "./AssetBatchesContext";
import { useAssetBatchesValueMappingContext } from "./AssetBatchesValueMappingContext";

interface ContextProps {
  editingComposition: TComposition;
  onLogoSelect: (mappingKey: string, logo: BrandLogo) => void;
  onSelectInputType: (mappingKey: string, value: TInputType) => void;
  onSelectInputValue: (mappingKey: string, value?: string) => void;
  onSelectInputValueSelect: (mappingKey: string, value: TColumnValue) => void;
  onSelectResizeType: (
    mappingKey: string,
    resizeType: TMediaResizeType,
  ) => void;
}

type ContextProviderProps = {
  children: ReactNode;
  editingComposition: TComposition;
};

const Context = createContext<ContextProps>({} as ContextProps);

const ContextProvider = ({
  children,
  editingComposition,
}: ContextProviderProps) => {
  const {
    currentStep,
    backgroundResizeTypes,
    setBackgroundResizeTypes,
    updateMaxStep,
  } = useAssetBatchesContext();

  const {
    updateCompositionVariables,
    setChangedMappingKey,
    onBackgroundMediaChange,
    onBackgroundColumnChange,
  } = useAssetBatchesValueMappingContext();

  const onLogoSelect = useCallback(
    (mappingKey: string, logo: BrandLogo) => {
      updateMaxStep(currentStep);
      const newMappingValues = (): TValueMappings => {
        const prevValueMapping = editingComposition.variables[mappingKey];
        return {
          ...editingComposition.variables,
          [mappingKey]: {
            ...prevValueMapping,
            value: {
              ...logo,
              id: logo.id,
              type: "logo",
            },
          },
        };
      };
      updateCompositionVariables(
        newMappingValues(),
        editingComposition.compositionId,
      );
    },
    [
      updateCompositionVariables,
      updateMaxStep,
      currentStep,
      editingComposition,
    ],
  );

  const onSelectInputType = useCallback(
    (mappingKey: string, value: TInputType) => {
      updateMaxStep(currentStep);
      setChangedMappingKey(mappingKey);
      const setValueMappings = () => {
        const prevValueMapping = editingComposition.variables[mappingKey];
        const shouldClearValue = value !== prevValueMapping.inputType;
        return {
          ...editingComposition.variables,
          [mappingKey]: {
            ...prevValueMapping,
            inputType: value,
            value: shouldClearValue ? undefined : prevValueMapping.value,
          },
        };
      };
      updateCompositionVariables(
        setValueMappings(),
        editingComposition.compositionId,
      );
    },
    [
      updateCompositionVariables,
      updateMaxStep,
      currentStep,
      editingComposition,
      setChangedMappingKey,
    ],
  );

  /**
   * This function only handles string | TColumnValue except regex input.
   * @param mappingKey
   * @param value TColumnValue type except regex input
   */
  const onSelectInputValue = useCallback(
    (mappingKey: string, value?: string) => {
      updateMaxStep(currentStep);
      setChangedMappingKey(mappingKey);
      const setValueMappings = () => {
        const prevValueMapping = editingComposition.variables[mappingKey];
        const val =
          isColumnValue(prevValueMapping.value) &&
          prevValueMapping.value.type === "regex"
            ? { ...prevValueMapping.value, regexPattern: value }
            : value;
        const inputType =
          typeof val === "string" ? "text" : prevValueMapping.inputType;
        return {
          ...editingComposition.variables,
          [mappingKey]: {
            ...prevValueMapping,
            value: val,
            inputType,
          },
        };
      };
      updateCompositionVariables(
        setValueMappings(),
        editingComposition.compositionId,
      );

      const valueMapping = editingComposition.variables[mappingKey];

      if (valueMapping.variable.variable === "Theme Background") {
        if (!value) {
          onBackgroundMediaChange(editingComposition.compositionId, undefined);
          return;
        }
        const media = getBackgroundMediaForUrl(value);
        onBackgroundMediaChange(editingComposition.compositionId, media);
      }
    },
    [
      updateCompositionVariables,
      updateMaxStep,
      currentStep,
      onBackgroundMediaChange,
      editingComposition,
      setChangedMappingKey,
    ],
  );

  const onSelectInputValueSelect = useCallback(
    (mappingKey: string, value: TColumnValue) => {
      updateMaxStep(currentStep);
      setChangedMappingKey(mappingKey);
      const setValueMappings = (): TValueMappings => {
        const prevValueMapping = editingComposition.variables[mappingKey];
        return {
          ...editingComposition.variables,
          [mappingKey]: {
            ...prevValueMapping,
            inputType: "match_to_column",
            value,
          },
        };
      };

      updateCompositionVariables(
        setValueMappings(),
        editingComposition.compositionId,
      );

      const valueMapping = editingComposition.variables[mappingKey];
      if (valueMapping.variable.variable === "Theme Background") {
        onBackgroundColumnChange(
          editingComposition.compositionId,
          value.column,
        );
      }
    },
    [
      updateCompositionVariables,
      updateMaxStep,
      currentStep,
      onBackgroundColumnChange,
      editingComposition,
      setChangedMappingKey,
    ],
  );

  const onSelectResizeType = useCallback(
    (mappingKey: string, resizeType: TMediaResizeType) => {
      updateMaxStep(currentStep);
      setChangedMappingKey(mappingKey);
      const setValueMappings = () => {
        const prevValueMapping = editingComposition.variables[mappingKey];
        return {
          ...editingComposition.variables,
          [mappingKey]: {
            ...prevValueMapping,
            resizeType,
          },
        };
      };
      updateCompositionVariables(
        setValueMappings(),
        editingComposition.compositionId,
      );
      if (
        editingComposition.variables[mappingKey].variable.variable ===
        "Theme Background"
      )
        setBackgroundResizeTypes({
          ...backgroundResizeTypes,
          [editingComposition.compositionId]: resizeType,
        });
    },
    [
      updateMaxStep,
      currentStep,
      setChangedMappingKey,
      updateCompositionVariables,
      editingComposition.compositionId,
      editingComposition.variables,
      setBackgroundResizeTypes,
      backgroundResizeTypes,
    ],
  );

  return (
    <Context.Provider
      value={{
        editingComposition,
        onLogoSelect,
        onSelectInputType,
        onSelectInputValue,
        onSelectInputValueSelect,
        onSelectResizeType,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const AssetBatchesCompositionProvider = memo(ContextProvider);

export const useAssetBatchesCompositionContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("Context must be used within a ContextProvider");
  }

  return context;
};
